import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Descriptions, Flex, Form, Modal, Select, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { getHarnessVersions, upgradeHarnessOrganization } from '@/apps/harness/services/harness';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import HarnessUpgradeFeedback from './HarnessUpgradeFeedback';

const HarnessVersionModal = ({
  organization,
  open,
  unavailable,
  statusError,
  onRefreshStatus,
  onClose,
  onClosed,
}) => {
  const { organizationId } = useOrganizationPath();
  const { session } = useAppContext();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState();
  const failedTag =
    organization?.upgrade?.status === 'failed' ? organization.upgrade.target?.tag : undefined;
  const selectedTag = selection ?? failedTag;
  const retry = !!failedTag && selectedTag === failedTag;
  const versionsKey = ['harness', organizationId, session.user.id, 'versions'];
  const versions = useInfiniteQuery({
    queryKey: versionsKey,
    queryFn: ({ pageParam }) => getHarnessVersions({ organizationId, next: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (page) => page.next,
    enabled: open,
    refetchOnMount: 'always',
    retry: false,
    refetchOnWindowFocus: false,
  });
  const update = useMutation({
    mutationFn: () =>
      upgradeHarnessOrganization({
        organizationId,
        ...(retry ? { retry: true } : { tag: selectedTag }),
      }),
    retry: false,
    onSuccess: () => {
      setSelection(undefined);
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: ['harness', organizationId],
        predicate: (query) => query.queryKey.at(-1) !== 'versions',
      }),
  });
  const options = [
    ...new Map(
      [
        ...(failedTag ? [{ tag: failedTag }] : []),
        ...(versions.data?.pages || []).flatMap((page) => page.items),
      ].map(({ tag }) => [tag, { label: tag, value: tag }]),
    ).values(),
  ];
  const valid =
    retry ||
    (!versions.isError &&
      !versions.isFetching &&
      options.some(({ value }) => value === selectedTag));
  const hasInstances = organization?.instances?.some((row) => row.displayStatus !== 'not_created');
  return (
    <Modal
      title="更新版本"
      open={open}
      afterClose={onClosed}
      width={520}
      okText="确认更新"
      cancelText="取消"
      confirmLoading={update.isPending}
      closable={!update.isPending}
      mask={{ closable: !update.isPending }}
      keyboard={!update.isPending}
      okButtonProps={{
        disabled: unavailable || !valid,
        'aria-label': '确认更新',
      }}
      cancelButtonProps={{ disabled: update.isPending, 'aria-label': '取消' }}
      onCancel={onClose}
      onOk={() => update.mutate()}
    >
      <Flex vertical gap="large">
        <Descriptions
          column={1}
          items={[
            {
              key: 'update',
              label: '最近更新',
              children: organization ? (
                <HarnessUpgradeFeedback upgrade={organization.upgrade} />
              ) : (
                '暂不可用'
              ),
            },
            {
              key: 'version',
              label: '当前版本',
              children: (
                <Typography.Text aria-label="当前版本">
                  {organization ? organization.target?.tag || '首次使用时自动选择' : '暂不可用'}
                </Typography.Text>
              ),
            },
          ]}
        />
        {statusError && (
          <Alert
            type="error"
            showIcon
            title="状态暂时无法确认"
            action={
              <Button onClick={onRefreshStatus} icon={<ReloadOutlined />}>
                重试查询状态
              </Button>
            }
          />
        )}
        <Form layout="vertical">
          <Form.Item label="目标版本">
            <Select
              aria-label="创作助手版本"
              placeholder="请选择版本"
              showSearch
              optionFilterProp="label"
              value={selectedTag}
              options={options}
              loading={versions.isFetching}
              disabled={unavailable || update.isPending}
              onChange={(value) => {
                setSelection(value);
                update.reset();
              }}
            />
          </Form.Item>
          <Flex gap="small">
            {versions.isError && (
              <Button
                aria-label="重试加载版本"
                icon={<ReloadOutlined />}
                disabled={versions.isFetching || update.isPending}
                onClick={() => {
                  versions.refetch();
                }}
              >
                重试加载版本
              </Button>
            )}
            {versions.hasNextPage && (
              <Button
                icon={<DownloadOutlined />}
                loading={versions.isFetchingNextPage}
                disabled={versions.isFetching || update.isPending}
                onClick={() => versions.fetchNextPage()}
              >
                加载更多版本
              </Button>
            )}
          </Flex>
        </Form>
        {versions.isError && (
          <Alert type="error" showIcon title="版本列表加载失败" description="请重试加载版本。" />
        )}
        {update.isError && (
          <Alert
            type="error"
            showIcon
            title="更新请求失败"
            description="请稍后重试；持续失败时刷新组织状态。"
          />
        )}
        {hasInstances && (
          <Typography.Text type="secondary">
            更新会中断成员实例的连接和正在执行的任务。
          </Typography.Text>
        )}
      </Flex>
    </Modal>
  );
};
HarnessVersionModal.propTypes = {
  organization: PropTypes.shape({
    target: PropTypes.shape({ tag: PropTypes.string }),
    upgrade: PropTypes.shape({
      status: PropTypes.string,
      target: PropTypes.shape({ tag: PropTypes.string }),
    }),
    instances: PropTypes.arrayOf(PropTypes.shape({ displayStatus: PropTypes.string })),
  }),
  statusError: PropTypes.bool,
  onRefreshStatus: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  unavailable: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onClosed: PropTypes.func.isRequired,
};
export default HarnessVersionModal;
